import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import { IconChevronDown, IconChevronUp, IconPhoneCall } from '@tabler/icons-react'

import { BasicModal } from '@/components/layout/modals/BasicModal'
import { Button } from '@/components/buttons/Button'
import { TextInput } from '@/components/forms/TextInput'
import { TextArea } from '@/components/forms/TextArea'
import { startDemoCall } from '@/reducers/voiceAgents/voiceAgents.actions'
import { getSelectedAgent } from '@/reducers/voiceAgents/voiceAgents.helpers'

import { useAgentDisplayName } from '../hooks/useAgentDisplayName'

import { useAgentOrb } from '../hooks/useAgentOrb'

import CallOrb from './CallOrb'

// E.164: leading +, no leading zero, 8–15 digits total.
const E164_PATTERN = /^\+[1-9]\d{7,14}$/

const EXAMPLE_NUMBER = '+15551234567'

const METADATA_PLACEHOLDER = `{
  "caller_first_name": "Immanuel",
  "case_type": "horse carriage accident"
}`

// A list rather than an object so the prefill keeps the authored key order.
const buildMetadataPrefill = (callMetadata) => {
  if (isEmpty(callMetadata)) {
    return ''
  }
  const seed = {}
  callMetadata.forEach((field) => {
    seed[field.key] = field.example
  })
  return JSON.stringify(seed, null, 2)
}

// Empty text is a valid "no metadata" ({}); otherwise the text must parse to
// a JSON object — Cartesia's call body takes an object, not an array/scalar,
// and the BE rejects anything else with a 422.
const parseMetadata = (text) => {
  const raw = text.trim()
  if (!raw) {
    return { value: {}, error: null }
  }
  try {
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { value: null, error: 'Metadata must be a JSON object, e.g. {"key": "value"}' }
    }
    return { value: parsed, error: null }
  } catch {
    return { value: null, error: 'Metadata is not valid JSON' }
  }
}

// Outbound call (AVR-428): the agent dials a number you enter, over telephony.
//
// A modal rather than the full-screen call view, and not because it is smaller
// work: there is nothing to watch here. The conversation happens on someone
// else's phone, so this is a form — a number, optional metadata — that you
// submit and are done with.
//
// It carries the agent's blob anyway. Placing a call is the moment you most
// want to be sure *which* agent is about to dial a real phone, and the blob is
// how that is answered everywhere else in Studio.
const DemoCallModal = ({ show, onClose }) => {
  const dispatch = useDispatch()
  const agentId = useSelector((state) => state.voiceAgents.selectedAgentId)
  const agent = useSelector((state) => getSelectedAgent(state.voiceAgents))
  const { displayName } = useAgentDisplayName()
  const palette = useAgentOrb()
  const [phoneNumber, setPhoneNumber] = useState('')
  const [metadataText, setMetadataText] = useState('')
  const [metadataOpen, setMetadataOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const wasOpen = useRef(false)

  const callMetadata = agent?.callMetadata ?? []
  const declaredKeys = callMetadata.map((field) => field.key)
  const metadataPrefill = buildMetadataPrefill(callMetadata)

  // The component stays mounted across open/close cycles (BasicModal only
  // unmounts its inner Modal), so clear the form on close — a stale number is
  // a footgun for a feature that dials live phones.
  // Seeded once per open, so a refetch can't overwrite edits mid-session.
  useEffect(() => {
    if (show) {
      if (!wasOpen.current) {
        wasOpen.current = true
        setMetadataText(metadataPrefill)
        setMetadataOpen(!isEmpty(metadataPrefill))
      }
      return
    }
    wasOpen.current = false
    setPhoneNumber('')
    setMetadataText('')
    setMetadataOpen(false)
    setSubmitting(false)
  }, [show, metadataPrefill])

  const trimmed = phoneNumber.trim()
  const isValidPhone = E164_PATTERN.test(trimmed)
  const { value: metadata, error: metadataError } = parseMetadata(metadataText)
  const canCall = isValidPhone && !metadataError && Boolean(agentId)

  // Undeclared keys are forwarded and ignored, so this warns without touching
  // `canCall`. Meaningless without a contract, where every key is "unknown".
  const unknownKeys = isEmpty(declaredKeys)
    ? []
    : Object.keys(metadata ?? {}).filter((key) => !declaredKeys.includes(key))

  const handleCall = async () => {
    if (!canCall || submitting) {
      return
    }
    setSubmitting(true)
    try {
      const result = await dispatch(startDemoCall(agentId, trimmed, metadata))
      if (result) {
        onClose()
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <BasicModal
      title="Outbound call"
      show={show}
      onClose={onClose}
      size="tiny"
      className="voice-agents__outbound-modal"
      footer={
        <div className="voice-agents__outbound-footer">
          <Button onClick={onClose} data-testid="demo-call-cancel-button">
            Cancel
          </Button>
          <Button
            primary
            onClick={handleCall}
            loading={submitting}
            disabled={!canCall}
            disabledTooltip={
              !isValidPhone
                ? `Enter a phone number in international format, e.g. ${EXAMPLE_NUMBER}`
                : metadataError || undefined
            }
            data-testid="demo-call-submit-button"
          >
            <IconPhoneCall /> Start call
          </Button>
        </div>
      }
    >
      <div className="voice-agents__outbound">
        {/* The agent's name, the same one the Studio page is titled with. The
            PE id was here first and was the wrong answer: you pick the agent by
            name everywhere else, and being shown an id at the moment you dial a
            real number invites checking it against something. */}
        <p className="voice-agents__outbound-agent">{displayName || agentId}</p>

        <div className="voice-agents__outbound-orb">
          <CallOrb palette={palette} size="9.375rem" animating={false} />
        </div>

        <p className="voice-agents__outbound-lede">
          Togo will place an outbound call to the number below. Use international format, for
          example <strong>{EXAMPLE_NUMBER}</strong>.
        </p>

        <label className="voice-agents__outbound-label" htmlFor="outbound-phone">
          Phone number
        </label>
        <TextInput
          id="outbound-phone"
          type="tel"
          placeholder={EXAMPLE_NUMBER}
          value={phoneNumber}
          onChange={(event) => setPhoneNumber(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              handleCall()
            }
          }}
          autoFocus
          data-testid="demo-call-phone-input"
        />

        {/* Empty, an open JSON textarea reads as something you have to fill in;
            prefilled, it is what you check before dialling. */}
        <button
          type="button"
          className="voice-agents__outbound-disclosure"
          onClick={() => setMetadataOpen((open) => !open)}
          aria-expanded={metadataOpen}
          aria-controls="outbound-metadata"
          data-testid="demo-call-metadata-toggle"
        >
          <span>Call metadata (optional)</span>
          {metadataOpen ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
        </button>

        {metadataOpen && (
          <div id="outbound-metadata">
            <TextArea
              className="voice-agents__outbound-metadata"
              aria-label="Call metadata as a JSON object (optional)"
              placeholder={METADATA_PLACEHOLDER}
              value={metadataText}
              onChange={(event) => setMetadataText(event.target.value)}
              rows={5}
              spellCheck={false}
              data-testid="demo-call-metadata-input"
            />
            {/* One font, all lowercase, no inline code span. It is a caption
                under a field, and the monospace `call_request.metadata` made it
                read as three fragments rather than one sentence. */}
            <p className="voice-agents__outbound-hint">
              {isEmpty(declaredKeys) ? (
                <>
                  a json object passed to the agent as call_request.metadata — for example caller
                  name or case context. keys the agent doesn&apos;t expect are ignored.
                </>
              ) : (
                <>
                  prefilled with the keys this agent reads off call_request.metadata:{' '}
                  {declaredKeys.join(', ')}. replace the example values with real ones, or drop any
                  you don&apos;t have.
                </>
              )}
            </p>
            {!isEmpty(unknownKeys) && (
              <p
                className="voice-agents__outbound-warning"
                data-testid="demo-call-metadata-warning"
              >
                this agent reads none of these keys, so they will be ignored:{' '}
                {unknownKeys.join(', ')}
              </p>
            )}
            {metadataError && (
              <p className="voice-agents__outbound-error" data-testid="demo-call-metadata-error">
                {metadataError}
              </p>
            )}
          </div>
        )}
      </div>
    </BasicModal>
  )
}

export default DemoCallModal
